import { useMutation } from "@apollo/client";
import { MouseEventHandler } from "react";

import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { topicState } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { showConfetti } from "@helpers/helpers";

import toggleTopicStateMutation from "./graphql/toggle-topic-state-mutation";

const TopicCTA = ({
  topic,
  isStatic = false,
}: {
  topic: any;
  isStatic?: boolean;
}) => {
  const [toggleState] = useMutation(toggleTopicStateMutation);

  const handleClickToggle: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    if (isStatic) return;
    const newState =
      topic.state === topicState.closed ? topicState.open : topicState.closed;
    const isMarkingAsDiscussed = newState === topicState.closed;
    if (isMarkingAsDiscussed) {
      // Topic li id is `meeting-<meetingId>-topic-<topicId>`. Only scroll
      // to the next topic when it belongs to the SAME meeting — we don't
      // want to jump across meeting dates on the multi-meeting view
      // (e.g. profile 1-on-1 tab) when the user marks the last topic of
      // a meeting.
      const currentTopicEl = document.querySelector(
        `[data-testid="container-topic-${topic.id}"]`,
      );
      const currentMeetingPrefix =
        currentTopicEl?.id.match(/^meeting-\d+-/)?.[0];
      if (currentMeetingPrefix) {
        const sameMeetingTopics = Array.from(
          document.querySelectorAll(`[id^="${currentMeetingPrefix}topic-"]`),
        );
        const currentIdx = sameMeetingTopics.indexOf(currentTopicEl);
        const nextTopic = sameMeetingTopics[currentIdx + 1];
        nextTopic?.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }
    toggleState({
      variables: {
        topicId: topic.id,
        state: newState,
      },
      optimisticResponse: {
        createOrUpdateTopic: {
          topic: {
            ...topic,
            state: newState,
          },
        },
      },
    });
  };

  const isDiscussed = topic.state === topicState.closed;

  if (isStatic) return null;
  return (
    <Tooltip
      text={
        isDiscussed ? "Mark topic as not discussed" : "Mark topic as discussed"
      }
    >
      <button
        disabled={isStatic}
        onClick={(e) => {
          if (!isDiscussed) {
            showConfetti({
              confettiNumber: 40,
              confettiRadius: 4,
              confettiColors: ["#10b981", "#34d399", "#6ee7b7", "#a7f3d0"],
            });
          }
          handleClickToggle(e);
        }}
        className={classNames(
          "flex items-center justify-center h-6 w-6 shrink-0 rounded-sm",
          isDiscussed
            ? "text-emerald-600 bg-emerald-50 hover:bg-emerald-100"
            : "text-gray-400 hover:bg-gray-100 invisible group-hover/topic:visible group-focus-within/topic:visible",
        )}
        aria-label="Mark topic as discussed"
      >
        <TopicflowIcon type="check" className="h-4 w-4" />
      </button>
    </Tooltip>
  );
};

export default TopicCTA;
